import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, apiGet } from '@/api/client'
import type { Site, Location } from '@/types/jispman'

export const useTenantStore = defineStore('tenant', () => {
  const currentSite = ref<Site | null>(null)
  const sites = ref<Site[]>([])
  const loading = ref(false)

  const siteId = computed(() => currentSite.value?.id ?? null)
  // null when no site selected — callers must handle; never fall back to a default currency (CLAUDE.md §6)
  const currencyCode = computed(() => currentSite.value?.currencyCode ?? null)
  const managementMode = computed(() => currentSite.value?.managementMode ?? 'full')
  const isFullMode = computed(() => managementMode.value === 'full')

  function _setHeader(id: string | null) {
    if (id) {
      api.defaults.headers.common['x-site-id'] = id
    } else {
      delete api.defaults.headers.common['x-site-id']
    }
  }

  async function loadSites(): Promise<void> {
    loading.value = true
    try {
      const res = await apiGet<{ data: Site[]; meta: unknown }>('/sites')
      sites.value = res.data
    } finally {
      loading.value = false
    }
  }

  async function switchSite(siteId: string): Promise<void> {
    /**
     * Drop the previous tenant's setup readiness (ADR-084).
     *
     * It is cached per site, and a stale value is wrong in both directions: carrying a
     * finished site's state into a half-built one lets a new tenant past the gate, and
     * carrying a half-built one into an established site would lock a working operator out
     * of their own app. Imported lazily to keep the store graph acyclic — the setup store
     * does not import this one, but the router imports both.
     */
    const { useSetupStore } = await import('@/stores/setup')
    useSetupStore().clear()

    const found = sites.value.find(s => s.id === siteId)
    if (found) {
      currentSite.value = found
      _setHeader(siteId)
      localStorage.setItem('lastSiteId', siteId)
      return
    }
    loading.value = true
    try {
      const res = await api.get<{ data: Site }>(`/sites/${siteId}`)
      currentSite.value = res.data.data
      _setHeader(siteId)
      localStorage.setItem('lastSiteId', siteId)
    } finally {
      loading.value = false
    }
  }

  function clearSite() {
    currentSite.value = null
    _setHeader(null)
    localStorage.removeItem('lastSiteId')
  }

  async function init(): Promise<void> {
    await loadSites()
    const lastId = localStorage.getItem('lastSiteId')
    if (lastId && sites.value.some(s => s.id === lastId)) {
      await switchSite(lastId)
    } else if (sites.value.length === 1) {
      await switchSite(sites.value[0].id)
    }
  }

  return {
    currentSite,
    sites,
    loading,
    siteId,
    currencyCode,
    managementMode,
    isFullMode,
    loadSites,
    switchSite,
    clearSite,
    init,
  }
})
